import React from 'react';
import { Loader, AlertCircle, ListChecks } from 'lucide-react';
import TaskListFilters from './TaskListFilters';
import TaskRow from './TaskRow';
import Pagination from '../../Pagination';

const TaskList = ({
  tasks = [],
  pagination = {},
  filters = {},
  sortConfig = {},
  loading = false,
  error = null,
  onFilterChange,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onTaskUpdate,
  onTaskClick,
}) => {
  const handleClearFilters = () => {
    onFilterChange('status', null);
    onFilterChange('priority', null);
    onFilterChange('category', null);
    onFilterChange('integrationSource', null);
    onFilterChange('evidenceTier', null);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Tasks</h3>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <TaskListFilters
        filters={filters}
        onFilterChange={onFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Summary Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">
            {pagination.total || 0} {pagination.total === 1 ? 'Task' : 'Tasks'}
          </span>
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-600">Sort by:</span>
          <select
            value={sortConfig.sortBy || 'priority'}
            onChange={(e) => onSortChange(e.target.value, sortConfig.sortOrder)}
            className="px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="priority">Priority</option>
            <option value="status">Status</option>
            <option value="control_id">Control ID</option>
            <option value="created_at">Date Created</option>
          </select>

          <select
            value={sortConfig.sortOrder || 'desc'}
            onChange={(e) => onSortChange(sortConfig.sortBy, e.target.value)}
            className="px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
      </div>

      {/* Top Pagination */}
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          pageSize={pagination.pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          isLoading={loading}
        />
      )}

      {/* Task List */}
      {loading && tasks.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-teal-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading tasks...</span>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <ListChecks className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Tasks Found</h3>
          <p className="text-gray-500">
            {filters.status || filters.priority || filters.category || filters.integrationSource || filters.evidenceTier
              ? 'Try adjusting your filters to see more tasks.'
              : 'No compliance tasks have been created yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onTaskUpdate={onTaskUpdate}
              onTaskClick={onTaskClick}
            />
          ))}
        </div>
      )}

      {/* Bottom Pagination */}
      {pagination.totalPages > 1 && tasks.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          pageSize={pagination.pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          isLoading={loading}
        />
      )}
    </div>
  );
};

export default TaskList;
